import { cn } from "../../../utils/cn.js";
import { useSpeechRecognition } from "../../hooks/useSpeechRecognition.js";
import { ArrowUpIcon, MicrophoneIcon, StopIcon } from "@phosphor-icons/react";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/components/PromptInput/PromptInput.tsx
/** The send button's box, which Stop takes while the host's work runs. */
const ACTION_CLASS = "inline-flex h-[28px] w-[28px] items-center justify-center rounded-sm border-0 transition-colors";
const ACTION_ON = "cursor-pointer bg-background-neutral-bold text-text-primary-inverse hover:bg-background-neutral-bold-hover";
const ACTION_OFF = "cursor-not-allowed bg-background-disabled text-icon-disabled";
/**
* Append a dictated speech chunk to the current input text, inserting a single
* separator space only when the previous text doesn't already end in whitespace
* — so consecutive chunks read as "first second" rather than running together,
* while the first chunk picks up no stray leading space. The chunk is trimmed;
* a blank / whitespace-only chunk leaves the value unchanged.
*/
const appendSpeechChunk = (prev, chunk) => {
	const text = chunk.trim();
	if (!text) return prev;
	return prev && !/\s$/.test(prev) ? `${prev} ${text}` : `${prev}${text}`;
};
/**
* Multi-line prompt input with a send button and Enter-to-submit
* (Shift+Enter for newline). Self-managed value — pass `onSubmit` and the
* component takes care of clearing on success.
*
* Forwards a ref exposing `focus()` so hosts can drop the operator straight
* into the textarea (e.g. when a panel opens).
*/
const PromptInput = forwardRef(({ className, disabled = false, disabledReason, enableVoice, hint, isStopping, isSubmitting, leadingActions, onStop, onSubmit, placeholder = "Message copilot…", rows = 2 }, ref) => {
	const [value, setValue] = useState("");
	const textAreaRef = useRef(null);
	const canSubmit = value.trim().length > 0 && !isSubmitting && !disabled && !onStop;
	useImperativeHandle(ref, () => ({ focus: () => textAreaRef.current?.focus() }), []);
	const handleSubmit = useCallback(() => {
		if (!canSubmit) return;
		onSubmit(value.trim());
		setValue("");
	}, [
		canSubmit,
		onSubmit,
		value
	]);
	const handleKeyDown = useCallback((event) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			handleSubmit();
		}
	}, [handleSubmit]);
	const { supported: voiceSupported, listening, start, stop } = useSpeechRecognition({ onResult: useCallback((chunk) => {
		setValue((prev) => appendSpeechChunk(prev, chunk));
	}, []) });
	const showVoice = Boolean(enableVoice) && voiceSupported;
	return /* @__PURE__ */ jsxs("div", {
		className: cn("relative flex flex-col gap-xs rounded-sm border border-(length:--border-width-default) border-border-input bg-elevation-surface-primary p-xs transition-colors focus-within:border-border-focused", className),
		children: [/* @__PURE__ */ jsx("textarea", {
			ref: textAreaRef,
			value,
			onChange: (e) => setValue(e.target.value),
			onKeyDown: handleKeyDown,
			placeholder: disabled && disabledReason ? disabledReason : placeholder,
			rows,
			disabled,
			"aria-disabled": disabled,
			className: "w-full resize-none border-0 bg-transparent text-body-base leading-relaxed text-text-primary outline-none placeholder:text-text-tertiary disabled:cursor-not-allowed"
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex items-center justify-between",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center gap-xs",
				children: [
					leadingActions,
					showVoice && /* @__PURE__ */ jsx("button", {
						type: "button",
						onClick: () => {
							if (isSubmitting || disabled) return;
							if (listening) stop();
							else start();
						},
						disabled: isSubmitting || disabled,
						title: listening ? "Stop dictation" : "Dictate with your voice",
						"aria-label": listening ? "Stop dictation" : "Dictate with your voice",
						"aria-pressed": listening,
						className: `inline-flex h-[20px] w-[20px] cursor-pointer items-center justify-center rounded-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${listening ? "bg-background-danger text-icon-danger hover:bg-background-danger-hover" : "text-icon-tertiary hover:bg-background-neutral-primary hover:text-icon-primary"}`,
						children: /* @__PURE__ */ jsx(MicrophoneIcon, {
							size: 13,
							weight: listening ? "fill" : "bold"
						})
					}),
					/* @__PURE__ */ jsx("span", {
						className: "text-body-xsmall text-text-tertiary",
						children: listening ? "Listening…" : hint ?? ""
					})
				]
			}), onStop ? /* @__PURE__ */ jsx("button", {
				type: "button",
				onClick: onStop,
				disabled: isStopping,
				"aria-label": "Stop",
				className: cn(ACTION_CLASS, isStopping ? ACTION_OFF : ACTION_ON),
				children: /* @__PURE__ */ jsx(StopIcon, {
					size: 14,
					weight: "fill"
				})
			}) : /* @__PURE__ */ jsx("button", {
				type: "button",
				onClick: handleSubmit,
				disabled: !canSubmit,
				"aria-label": "Send",
				className: cn(ACTION_CLASS, canSubmit ? ACTION_ON : ACTION_OFF),
				children: /* @__PURE__ */ jsx(ArrowUpIcon, {
					size: 14,
					weight: "bold"
				})
			})]
		})]
	});
});
PromptInput.displayName = "PromptInput";
//#endregion
export { PromptInput, appendSpeechChunk };

//# sourceMappingURL=PromptInput.js.map